import { useState } from 'react';
import type { Entry } from '@ozone/shared';
import { CATEGORY_ORDER, formatDealSize } from './compose';
import {
  applyFilters,
  distinctSubmitters,
  monthLabelFor,
  monthOptions,
  type FilterState,
} from './filters';
import { FilterBar } from './components/FilterBar';
import { LoadingState } from './components/states/LoadingState';
import { ErrorState } from './components/states/ErrorState';

/**
 * The signed-in identity as returned by `/api/auth/me` (AOZONE-90). `role`
 * drives which SPA view renders; `slackUserId` is the caller's bridged
 * submitter id, used for the own/all split in TeamView.
 */
export interface Me {
  email?: string;
  role?: 'admin' | 'contributor';
  slackUserId?: string;
}

interface TeamViewProps {
  entries: Entry[];
  me: Me | null;
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onSearchChange: (raw: string) => void;
  now: Date;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

/** Characters of narrative shown before truncation (AOZONE-91 §6.3). */
const NARRATIVE_LIMIT = 140;

/**
 * The table's columns, in order. Single source of truth: the header cells are
 * rendered from this, and the AOZONE-114 action-item row spans
 * `TEAM_COLUMNS.length`, so adding or removing a column cannot leave that row's
 * colSpan stale.
 */
const TEAM_COLUMNS = [
  'Submitter',
  'Period',
  'Title',
  'Narrative',
  'Customer / Partner',
  'Deal size',
] as const;

/**
 * AOZONE-91: the compact, dense, read-only contributor view (RFC §6.3). This is
 * deliberately NOT a permission-gated fork of the admin UI — it renders NONE of
 * the mutation surfaces (`EntryRow`/`DraftEditor`/`SubmitterCard`/period picker/
 * include-in-MBR). It's a scan-optimized table grouped by canonical category
 * order, with the existing FilterBar plus an own/all toggle (§6.1a).
 *
 * AOZONE-114: it also renders each entry's action item, on a full-width row
 * beneath the entry. Read-only does not mean write-only-fields: the action item
 * is a REQUIRED field for three of the six categories, and omitting it here
 * meant contributors were compelled to write something they could never read
 * back. Keep the rendered field set in sync with what submitters are asked for.
 */
export function TeamView({
  entries,
  me,
  filters,
  onChange,
  onSearchChange,
  now,
  loading,
  error,
  onRetry,
}: TeamViewProps) {
  // §6.1a: default is the caller's OWN entries; `mine === false` shows all.
  // The split is client-side, keyed on the bridged slackUserId. Defensive: if
  // we somehow lack the caller's slackUserId (shouldn't happen for a bridged
  // contributor), fall back to show-all rather than a silently-empty own view.
  const showAll = filters.mine === false;
  const mySlackId = me?.slackUserId;
  const restrictToOwn = !showAll && !!mySlackId;

  const monthEntries = entries.filter((e) => (e.mbrPeriod ?? '') === filters.month);
  // Scope the submitter dropdown to what's actually reachable: in own-mode
  // that's just the caller, so picking someone else can't become a dead no-op.
  const submitters = distinctSubmitters(
    restrictToOwn ? monthEntries.filter((e) => e.submitterId === mySlackId) : monthEntries,
  );

  const filtered = applyFilters(entries, filters).filter(
    (e) => !restrictToOwn || e.submitterId === mySlackId,
  );

  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    rows: filtered.filter((e) => e.entryCategory === category),
  })).filter((g) => g.rows.length > 0);

  return (
    <>
      <FilterBar
        state={filters}
        categories={CATEGORY_ORDER}
        submitters={submitters}
        months={monthOptions(now)}
        onChange={onChange}
        onSearchChange={onSearchChange}
      />
      <div className="sticky top-[6.5rem] z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1280px] items-center gap-3 px-6 py-2 text-sm text-slate-700">
          <label className="inline-flex items-center gap-2 font-medium">
            <input
              type="checkbox"
              aria-label="Show all entries"
              checked={showAll}
              onChange={(e) => onChange({ ...filters, mine: e.target.checked ? false : true })}
              className="h-4 w-4 shrink-0 accent-slate-900"
            />
            Show all entries
          </label>
          <span className="text-slate-400">
            {showAll ? 'Showing the full team' : 'Showing only your entries'}
          </span>
        </div>
      </div>
      <main className="mx-auto max-w-[1280px] px-6 py-8">
        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} />}
        {!loading && !error && groups.length === 0 && (
          <p className="py-16 text-center text-sm text-slate-500">
            No entries for {monthLabelFor(filters.month)}
            {showAll ? '' : ' from you'}.
          </p>
        )}
        {!loading && !error && groups.length > 0 && (
          <div className="flex flex-col gap-8">
            {groups.map((g) => (
              <section key={g.category}>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  {g.category}
                </h2>
                <div className="overflow-hidden rounded-md border border-slate-200">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                        {TEAM_COLUMNS.map((label) => (
                          <th key={label} className="px-3 py-2 font-medium">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((entry) => (
                        <TeamRow key={entry.entryId} entry={entry} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </>
  );
}

function TeamRow({ entry }: { entry: Entry }) {
  const [expanded, setExpanded] = useState(false);
  const narrative = entry.narrativeBody ?? '';
  const needsTruncate = narrative.length > NARRATIVE_LIMIT;
  const shown =
    expanded || !needsTruncate ? narrative : `${narrative.slice(0, NARRATIVE_LIMIT)}…`;
  const customerOrPartner = entry.customerName ?? entry.partnerOrCloud ?? '';
  const dealSize = formatDealSize(entry);
  // AOZONE-114. Trimmed, not merely truthy: a whitespace-only value would
  // otherwise render a bare "Action:" label with nothing after it.
  const actionItem = entry.actionItem?.trim();

  return (
    <>
      <tr data-entry-id={entry.entryId} className="border-t border-slate-100 align-top">
        <td className="px-3 py-2 font-medium text-slate-800">{entry.submitterAlias}</td>
        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
          {entry.mbrPeriod ?? ''}
        </td>
        <td className="px-3 py-2 text-slate-800">{entry.title ?? ''}</td>
        <td className="px-3 py-2 text-slate-600">
          {narrative &&
            (needsTruncate ? (
              <button
                type="button"
                aria-label={expanded ? 'Collapse narrative' : 'Expand narrative'}
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
                className="text-left hover:text-slate-900"
              >
                {shown}
              </button>
            ) : (
              <span>{shown}</span>
            ))}
        </td>
        <td className="px-3 py-2 text-slate-700">{customerOrPartner}</td>
        <td className="px-3 py-2 whitespace-nowrap text-slate-700">{dealSize ?? ''}</td>
      </tr>
      {/*
        AOZONE-114: the action item, on its own full-width row under the entry.
        Rendered verbatim rather than truncated — it is required for three of
        the six categories and can run to ~500 chars, and a full-width row fits
        that without either a click-to-expand or squeezing the six columns above
        (a 7th column measured 158px off the Narrative column). Mirrors the
        admin view's "Action:" label (components/EntryRow.tsx). Omitted entirely
        when there is no action item, so no empty cell or dangling label.
      */}
      {actionItem && (
        <tr data-entry-id={entry.entryId} className="align-top">
          {/*
            An action item can be one unbroken token — a URL, a path, a hash —
            with no wrap opportunity. The table wrapper (the `overflow-hidden`
            div that this table sits in, rendered in `TeamView` above) therefore
            CLIPS SILENTLY rather than scrolling: measured at 3379px
            of table against a 730px wrapper. Prose fixtures cannot catch it,
            because prose has spaces to break at.

            `wrap-anywhere` (overflow-wrap: anywhere), NOT `break-words`.
            Measured: `break-words` (overflow-wrap: break-word) leaves it at
            3379px — it does not reduce min-content width, so under auto table
            layout the token still forces the table wider than its wrapper.
            `anywhere` does reduce it: 730px, exactly the wrapper. `break-all`
            also measures 730px but breaks ordinary prose mid-word, which is the
            wrong trade for a field that is usually a sentence.
          */}
          <td
            colSpan={TEAM_COLUMNS.length}
            className="px-3 pb-2 pt-0 text-xs text-slate-600 wrap-anywhere"
          >
            <strong className="font-semibold text-slate-900">Action:</strong>{' '}
            {actionItem}
          </td>
        </tr>
      )}
    </>
  );
}
